import { useTranslation } from 'react-i18next';
import { VdsButton } from '@volt/volt-extended-components-react';
import { InvestigationStatus } from '@customer/store/apis/investigationsApi';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AssetGIModel,
  AssetVIModel,
  Investigatable,
  useInvestigateMutation,
} from '@customer/store/apis/interactionsApi';
import { ReactElement, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { Permission, userHasPermission } from '@customer/permissions';
import { ModelName } from '@customer/store/types';

const modelName = ModelName.Investigation;

const getHasOpenInvestigation = (interaction: Investigatable | undefined): boolean => {
  return !!interaction?.invest && interaction.invest.st === InvestigationStatus.Open;
};

interface InvestigationButtonProps {
  interaction?: AssetGIModel | AssetVIModel;
}

export const InvestigationButton = ({
  interaction,
}: InvestigationButtonProps): ReactElement | undefined => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { assetId } = useParams();

  const userData = useAppSelector(state => state.auth);

  const [postInvestigate] = useInvestigateMutation();

  const [isWaiting, setIsWaiting] = useState(false);

  const goToInvestigation = useCallback(
    (investigationId: number) => {
      dispatch(closeSidePanel());
      navigate(`/investigation/${investigationId}`);
    },
    [dispatch, navigate],
  );

  const onContinueInvestigation = useCallback(() => {
    // Check that there is an open investigation associated with this interaction.
    if (interaction?.invest?.id && interaction?.invest?.st === InvestigationStatus.Open) {
      goToInvestigation(interaction.invest.id);
    }
  }, [goToInvestigation, interaction?.invest?.st, interaction?.invest?.id]);

  const onStartInvestigation = useCallback(async () => {
    // Check that there is no investigation associated with this interaction.
    if (interaction && !interaction.invest && assetId) {
      // Set disabled button state.
      setIsWaiting(true);

      let response: number | null = null;

      try {
        response = await postInvestigate({
          assetId: assetId,
          interactionId: interaction.id,
        }).unwrap();
      } catch (error) {
        console.debug(error);
      }

      if (typeof response === 'number') {
        // Don't remove disabled button state or it will trigger a re-render.
        goToInvestigation(response);
      } else {
        // Remove disabled button state.
        setIsWaiting(false);
      }
    }
  }, [interaction, postInvestigate, assetId, goToInvestigation]);

  const hasInvestigation = !!interaction?.invest;
  const hasOpenInvestigation = getHasOpenInvestigation(interaction);
  const investigationCannotBeActioned = hasInvestigation && !hasOpenInvestigation;
  const isDisabled = investigationCannotBeActioned || isWaiting;

  if (!userHasPermission(userData, Permission.InvestigationsAll)) return undefined;

  return (
    <VdsButton
      onClick={
        interaction
          ? hasOpenInvestigation
            ? onContinueInvestigation
            : !hasInvestigation
              ? onStartInvestigation
              : undefined
          : undefined
      }
      variant="primary"
      size="medium"
      isFullWidth
      disabled={isDisabled}
    >
      {hasOpenInvestigation
        ? t('Model.Continue', { modelName })
        : isWaiting
          ? t('Loading')
          : t('Model.Start', { modelName })}
    </VdsButton>
  );
};

export default InvestigationButton;
